import React from 'react';
import { Title, PasswordTextField } from '../../elements';
import { notifications, getLoginErrorMessage, parseAccountLockout, AccountLockoutInfo } from '../../utils';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import useAuthContext from '../../hooks/useAuthContext';
import { useTranslation } from 'react-i18next';
import { getAuthValueFromToken } from '../../utils/tokenHelper';
import { ApiService } from '../../hooks';
import { HealthSystemDomainUsersResultBusinessUnitResponse } from '../../api/HealthSystemApi';
import { BusinessUnitLabel } from '../../components';
import { brand } from '../../theme/brand';

const LoginNew: React.FC<any> = () => {
  const { t, i18n } = useTranslation();
  const { state, updateToken, updateRefreshToken, updateBusinessUnits } = useAuthContext();
  const navigate = useNavigate();

  const apiService = new ApiService();

  const [isSelectionModeActive, setIsSelectionModeActive] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [businessUnitId, setBusinessUnitId] = React.useState('');
  const [searchFilter, setSearchFilter] = React.useState('');

  const [emailValidation, setEmailValidation] = React.useState(false);
  const [passwordValidation, setPasswordValidation] = React.useState(false);
  const [lockout, setLockout] = React.useState<AccountLockoutInfo | null>(null);
  const [businessUnits, setBusinessUnits] = React.useState<HealthSystemDomainUsersResultBusinessUnitResponse[]>([]);

  const cleanValidation = () => {
    setEmailValidation(false);
    setPasswordValidation(false);
  };

  const filterBusinessUnits = (units: HealthSystemDomainUsersResultBusinessUnitResponse[]) => {
    if (!searchFilter.trim()) {
      return units;
    }

    const searchTerm = searchFilter.toLowerCase().trim();
    return units.filter((unit) => {
      const nameMatch = unit.name?.toLowerCase().includes(searchTerm);
      const numberMatch = unit.number?.toLowerCase().includes(searchTerm);
      const addressMatch = unit.address?.toLowerCase().includes(searchTerm);
      const typeMatch = unit.type?.toString().toLowerCase().includes(searchTerm);
      const roleMatch = unit.role?.toLowerCase().includes(searchTerm);

      return nameMatch || numberMatch || addressMatch || typeMatch || roleMatch;
    });
  };

  const isValidForm = (): boolean => {
    cleanValidation();
    let isValid = true;

    if (!email) {
      notifications.warning('Missing user email address');
      setEmailValidation(true);
      isValid = false;
    }

    if (!password) {
      notifications.warning('Missing user password');
      setPasswordValidation(true);
      isValid = false;
    }

    return isValid;
  };

  const handleLoginError = (e: unknown) => {
    const lockoutInfo = parseAccountLockout(e);
    if (lockoutInfo.isLocked) {
      setLockout(lockoutInfo);
    } else {
      setLockout(null);
      notifications.error(getLoginErrorMessage(e, t));
    }
    setIsBusy(false);
  };

  const proceedAuth = async () => {
    if (!isValidForm()) {
      return;
    }

    setIsBusy(true);

    await apiService.api.auth
      .authLogin({ email, password })
      .then(async (result) => {
        setIsBusy(false);
        setLockout(null);

        // Set business unit
        setBusinessUnits(result.data.businessUnits);
        updateBusinessUnits(result.data.businessUnits);

        // If there are no business units, show an error message
        if (result.data.businessUnits?.length === 0) {
          notifications.error(t('f-login-no-business-units'));
          setIsBusy(false);
          return;
        }

        // If there is only one business units, set it as the selected business unit and proceed to business unit auth
        if (result.data.businessUnits?.length === 1) {
          const id = result.data.businessUnits[0].id;
          setBusinessUnitId(id);
          proceedBusinessUnitsAuth(id);
          return;
        }

        // If there are multiple business units, show the selection mode
        setIsSelectionModeActive(true);
        setIsBusy(false);
      })
      .catch(handleLoginError);
  };

  const proceedBusinessUnitsAuth = async (selectedId?: string) => {
    const unitIdToUse = selectedId ?? businessUnitId;
    if (!unitIdToUse) {
      notifications.warning(t('f-login-select-business-unit'));
      return;
    }

    setIsBusy(true);

    await apiService.api.auth
      .authLoginBusinessUnit({ email, password, businessUnitId: unitIdToUse })
      .then(async (result) => {
        notifications.success(t('f-login-success'));

        setIsBusy(false);
        setLockout(null);

        updateToken(result.data.token);
        updateRefreshToken(result.data.refreshToken);

        i18n.changeLanguage(getAuthValueFromToken(result.data.token).locale);
      })
      .catch(handleLoginError);
  };

  return state.isLoggedIn ? (
    <Navigate to={{ pathname: '/dashboard' }} />
  ) : (
    // The widths are v4's (lg 1280px; sm 600px, md 960px), which this page was laid out against - v9 moved
    // lg and md, and spelling them out keeps the card where it was.
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <div className="-m-3 flex flex-wrap justify-center">
        <div className="box-border w-full p-3 min-[600px]:w-8/12 min-[960px]:w-6/12">
          <Paper
            elevation={2}
            sx={{ p: 3, display: 'flex', overflow: 'auto', flexDirection: 'column', minHeight: 375 }}
          >
            <div className="flex gap-2">
              <Avatar alt={brand.logoAlt} src={brand.logoSrc} />
              <Title>{`${brand.appName} Login`}</Title>
            </div>

            <hr />

            {lockout && (
              <Alert severity="warning" id="loginLockoutBanner" className="!mb-3">
                <div>
                  {lockout.until
                    ? t('f-login-locked-until', {
                        until: lockout.until.toLocaleString(i18n.language || undefined),
                        interpolation: { escapeValue: false }
                      })
                    : t('f-login-locked')}
                </div>
                <Button
                  id="lockoutForgotPasswordLink"
                  variant="text"
                  size="small"
                  color="primary"
                  className="!p-0 !mt-1"
                  onClick={() => navigate('/forgot-password')}
                >
                  {t('f-login-locked-hint')}
                </Button>
              </Alert>
            )}

            <div className="flex min-h-[250px] flex-col justify-center">
              {isSelectionModeActive ? (
                <>
                  <Typography variant="body1" gutterBottom>
                    {t('f-login-select-business-unit-title')}
                  </Typography>

                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={t('f-login-search-business-unit') || 'Search by name, number, address...'}
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchFilter && (
                          <InputAdornment position="end">
                            <ClearIcon style={{ cursor: 'pointer' }} onClick={() => setSearchFilter('')} />
                          </InputAdornment>
                        )
                      }
                    }}
                    sx={{ mb: 1 }}
                  />

                  <div className="my-2 flex items-center justify-between">
                    <Typography variant="caption" color="textSecondary">
                      {filterBusinessUnits(businessUnits).length} of {businessUnits.length} units
                    </Typography>
                    {searchFilter && (
                      <Chip
                        size="small"
                        label={t('f-login-clean-filter')}
                        onDelete={() => setSearchFilter('')}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </div>

                  <Box
                    sx={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '8px'
                    }}
                  >
                    <RadioGroup value={businessUnitId} onChange={(e) => setBusinessUnitId(e.target.value)}>
                      {filterBusinessUnits(businessUnits)
                        .filter((unit) => unit.id)
                        .sort((a, b) => {
                          const typeCompare = String(a.type).localeCompare(String(b.type));
                          if (typeCompare !== 0) return typeCompare;
                          return String(a.name).localeCompare(String(b.name));
                        })
                        .map((unit) => (
                          <div
                            key={unit.id}
                            className="flex items-center rounded p-1 transition-colors duration-200 hover:bg-black/[0.02]"
                          >
                            <FormControlLabel
                              value={unit.id}
                              control={<Radio color="primary" />}
                              label={<BusinessUnitLabel unit={unit} />}
                              style={{ margin: 0, width: '100%' }}
                            />
                          </div>
                        ))}
                      {filterBusinessUnits(businessUnits).length === 0 && (
                        <div className="py-6 text-center">
                          <Typography variant="body2" color="textSecondary">
                            {t('f-login-no-results') || 'No business units found matching your search'}
                          </Typography>
                        </div>
                      )}
                    </RadioGroup>
                  </Box>

                  <Button
                    id="userLoginBtn"
                    startIcon={<LockOpenIcon />}
                    sx={{ mt: 1 }}
                    disabled={isBusy || !businessUnitId}
                    variant="contained"
                    size="small"
                    color="primary"
                    fullWidth
                    type="submit"
                    onClick={() => proceedBusinessUnitsAuth(businessUnitId)}
                  >
                    {t('f-login-continue')}
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="info" style={{ marginBottom: '16px' }}>
                    <Typography variant="body2">{t('f-login-universal-access-info')}</Typography>
                  </Alert>

                  <TextField
                    variant="outlined"
                    // v4's dense margin also made the input dense; v9 splits that out into size.
                    size="small"
                    type="email"
                    margin="dense"
                    fullWidth
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    id="userEmail"
                    label={t('f-login-email')}
                    helperText={t('f-login-email-helper')}
                    error={emailValidation}
                  />

                  <PasswordTextField
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    id="userPass"
                    label={t('f-login-password')}
                    helperText={t('f-login-password-helper')}
                    error={passwordValidation}
                  />

                  <Button
                    id="userLoginBtn"
                    startIcon={<LockOpenIcon />}
                    sx={{ mt: 1 }}
                    disabled={isBusy}
                    variant="contained"
                    size="small"
                    color="primary"
                    fullWidth
                    type="submit"
                    onClick={proceedAuth}
                  >
                    {t('f-login-button')}
                  </Button>
                </>
              )}
            </div>

            <div className="flex justify-center">{isBusy === true && <CircularProgress size={72} />}</div>
          </Paper>
        </div>
      </div>
    </Container>
  );
};

export default LoginNew;
